/**
 * Origem da visita — captura links marcados (?utm_..., gclid, fbclid) e site de referência.
 * Rótulos em português para o admin (sem jargão).
 *
 * Regras (estilo GA):
 *  - A origem é decidida UMA vez por carregamento de documento, na primeira
 *    página: parâmetros de campanha > site de referência externo > herança.
 *  - Navegação interna (SPA ou link do próprio site, inclusive em aba nova)
 *    HERDA a origem da sessão em andamento — o próprio domínio nunca é origem.
 *  - Sessão = até 30 min sem atividade. Guardada no localStorage (vale entre
 *    abas); antes ficava no sessionStorage, que é por aba: abrir um link em aba
 *    nova virava "Entrada direta".
 *  - Retorno de login Google / checkout (accounts.google.com, Mercado Pago)
 *    também herda — não é visita nova vinda do Google.
 */

// Import relativo: mantém este módulo testável no vitest (sem alias "@/").
import { normalizeTelemetryPath } from './constants'

export interface VisitAttribution {
  /** Identificador normalizado (google, instagram, direct…) */
  source: string
  /** Nome legível no painel */
  sourceLabel: string
  medium: string | null
  mediumLabel: string | null
  campaign: string | null
  referrerHost: string | null
  landingPath: string
  /** Parâmetro de clique de anúncio presente na chegada (gclid, fbclid…), sem o valor. */
  clickId?: string | null
}

const STORAGE_KEY = 'cm_visit_origin_v2'
/** Mesmo corte de sessão usado no admin (lib/admin/telemetry-queries.ts). */
const SESSION_TIMEOUT_MS = 30 * 60 * 1000

const SOURCE_NAMES: Record<string, string> = {
  google: 'Google',
  instagram: 'Instagram',
  ig: 'Instagram',
  facebook: 'Facebook',
  fb: 'Facebook',
  meta: 'Facebook / Meta',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  twitter: 'X (Twitter)',
  x: 'X (Twitter)',
  tiktok: 'TikTok',
  whatsapp: 'WhatsApp',
  email: 'Email',
  newsletter: 'Newsletter',
  bing: 'Bing',
  direct: 'Entrada direta',
}

const MEDIUM_NAMES: Record<string, string> = {
  cpc: 'Anúncio pago',
  ppc: 'Anúncio pago',
  paid: 'Anúncio pago',
  email: 'Email',
  social: 'Rede social',
  organic: 'Busca orgânica',
  referral: 'Link de outro site',
  banner: 'Banner / display',
}

/**
 * IDs de clique de anúncio. Muitos cliques de anúncio chegam SEM referrer
 * (apps, iOS, Display/YouTube) — sem ler isto eles viravam "Entrada direta".
 */
const CLICK_IDS: Array<{ param: string; source: string; label: string; medium: string }> = [
  { param: 'gclid', source: 'google', label: 'Google — anúncio', medium: 'cpc' },
  { param: 'gbraid', source: 'google', label: 'Google — anúncio', medium: 'cpc' },
  { param: 'wbraid', source: 'google', label: 'Google — anúncio', medium: 'cpc' },
  { param: 'gad_source', source: 'google', label: 'Google — anúncio', medium: 'cpc' },
  { param: 'msclkid', source: 'bing', label: 'Bing — anúncio', medium: 'cpc' },
  { param: 'ttclid', source: 'tiktok', label: 'TikTok — anúncio', medium: 'cpc' },
  // fbclid vai em TODO link que sai do Facebook/Instagram (orgânico ou anúncio).
  { param: 'fbclid', source: 'facebook', label: 'Facebook', medium: 'social' },
]

/** Hosts que devolvem o visitante pra cá no meio da visita (login, pagamento). */
const RETURN_HOSTS = [
  'accounts.google.com',
  'accounts.youtube.com',
  'mercadopago.com',
  'mercadopago.com.br',
  'mercadolivre.com.br',
  'mpago.la',
  'checkout.stripe.com',
  'pay.google.com',
]

const OWN_DOMAIN = 'calculamei.com.br'

function cleanHost(host: string): string {
  return host.replace(/^www\./, '').toLowerCase()
}

function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`)
}

/** Próprio site (produção, www, preview/localhost no host atual). */
export function isOwnHost(host: string | null | undefined, currentHost?: string): boolean {
  if (!host) return false
  const h = cleanHost(host)
  if (hostMatches(h, OWN_DOMAIN) || h.includes('calculamei.')) return true
  return !!currentHost && h === cleanHost(currentHost)
}

function isReturnHost(host: string): boolean {
  return RETURN_HOSTS.some((d) => hostMatches(host, d))
}

function referrerHostOf(referrer: string): string | null {
  if (!referrer) return null
  try {
    return cleanHost(new URL(referrer).hostname) || null
  } catch {
    return null
  }
}

function inferFromReferrer(referrer: string): { source: string; sourceLabel: string; host: string | null } {
  if (!referrer) {
    return { source: 'direct', sourceLabel: SOURCE_NAMES.direct, host: null }
  }
  try {
    const host = cleanHost(new URL(referrer).hostname)
    // Gmail antes do "google." genérico (app Android: com.google.android.gm).
    if (host === 'mail.google.com' || host === 'com.google.android.gm') {
      return { source: 'email', sourceLabel: 'Email (Gmail)', host }
    }
    if (
      host.startsWith('outlook.') ||
      host.endsWith('mail.yahoo.com') ||
      host === 'mail.uol.com.br' ||
      host === 'webmail.terra.com.br'
    ) {
      return { source: 'email', sourceLabel: 'Email (webmail)', host }
    }
    if (host.includes('google.')) return { source: 'google', sourceLabel: 'Google (busca)', host }
    if (host.includes('instagram.') || host === 'l.instagram.com') {
      return { source: 'instagram', sourceLabel: 'Instagram', host }
    }
    if (host.includes('facebook.') || host.includes('fb.')) {
      return { source: 'facebook', sourceLabel: 'Facebook', host }
    }
    if (host.includes('youtube.') || host === 'youtu.be') {
      return { source: 'youtube', sourceLabel: 'YouTube', host }
    }
    if (host.includes('linkedin.') || host === 'lnkd.in') {
      return { source: 'linkedin', sourceLabel: 'LinkedIn', host }
    }
    if (host.includes('twitter.') || host === 't.co' || host === 'x.com') {
      return { source: 'twitter', sourceLabel: 'X (Twitter)', host }
    }
    if (host.includes('whatsapp.') || host === 'wa.me') {
      return { source: 'whatsapp', sourceLabel: 'WhatsApp', host }
    }
    if (host.includes('tiktok.')) return { source: 'tiktok', sourceLabel: 'TikTok', host }
    if (host.includes('bing.')) return { source: 'bing', sourceLabel: 'Bing (busca)', host }
    if (host === 'chatgpt.com' || host.endsWith('openai.com')) {
      return { source: 'chatgpt', sourceLabel: 'ChatGPT', host }
    }
    if (host.includes('perplexity.')) return { source: 'perplexity', sourceLabel: 'Perplexity', host }
    if (isOwnHost(host)) {
      return { source: 'internal', sourceLabel: 'Navegação interna', host }
    }
    return { source: host, sourceLabel: `Outro site: ${host}`, host }
  } catch {
    return { source: 'unknown', sourceLabel: 'Origem desconhecida', host: null }
  }
}

function labelSource(raw: string | null): string {
  if (!raw) return SOURCE_NAMES.direct
  const key = raw.toLowerCase().trim()
  return SOURCE_NAMES[key] ?? raw.charAt(0).toUpperCase() + raw.slice(1)
}

function labelMedium(raw: string | null): string | null {
  if (!raw) return null
  const key = raw.toLowerCase().trim()
  return MEDIUM_NAMES[key] ?? raw
}

function directAttribution(landingPath: string): VisitAttribution {
  return {
    source: 'direct',
    sourceLabel: SOURCE_NAMES.direct,
    medium: null,
    mediumLabel: null,
    campaign: null,
    referrerHost: null,
    landingPath,
    clickId: null,
  }
}

/**
 * Origem "nova" a partir da URL de chegada + referrer — ou null quando a
 * chegada não traz origem própria (sem referrer, próprio site, retorno de
 * login/pagamento) e portanto deve herdar a sessão em andamento.
 */
export function resolveLandingOrigin(input: {
  search: string
  referrer: string
  landingPath: string
  currentHost?: string
}): VisitAttribution | null {
  const params = new URLSearchParams(input.search)
  const utmSource = params.get('utm_source')?.trim() || null
  const utmMedium = params.get('utm_medium')?.trim() || null
  const utmCampaign = params.get('utm_campaign')?.trim() || null
  const clickId = CLICK_IDS.find((c) => params.get(c.param))

  const refHost = referrerHostOf(input.referrer)
  const externalRef =
    refHost && !isOwnHost(refHost, input.currentHost) && !isReturnHost(refHost) ? refHost : null

  if (utmSource) {
    let sourceLabel = labelSource(utmSource)
    const m = utmMedium?.toLowerCase()
    if (m === 'cpc' || m === 'paid' || m === 'ppc' || (!m && clickId?.medium === 'cpc')) {
      sourceLabel = `${sourceLabel} — anúncio`
    }
    return {
      source: utmSource.toLowerCase(),
      sourceLabel,
      medium: utmMedium,
      mediumLabel: labelMedium(utmMedium),
      campaign: utmCampaign,
      referrerHost: externalRef,
      landingPath: input.landingPath,
      clickId: clickId?.param ?? null,
    }
  }

  if (clickId) {
    // fbclid vindo do Instagram (referrer l.instagram.com) é Instagram, não Facebook.
    const fromInstagram = clickId.param === 'fbclid' && !!externalRef?.includes('instagram.')
    return {
      source: fromInstagram ? 'instagram' : clickId.source,
      sourceLabel: fromInstagram ? 'Instagram' : clickId.label,
      medium: utmMedium ?? clickId.medium,
      mediumLabel: labelMedium(utmMedium ?? clickId.medium),
      campaign: utmCampaign,
      referrerHost: externalRef,
      landingPath: input.landingPath,
      clickId: clickId.param,
    }
  }

  if (externalRef) {
    const inferred = inferFromReferrer(input.referrer)
    return {
      source: inferred.source,
      sourceLabel: inferred.sourceLabel,
      medium: null,
      mediumLabel: null,
      campaign: utmCampaign,
      referrerHost: inferred.host,
      landingPath: input.landingPath,
      clickId: null,
    }
  }

  return null
}

type StoredOrigin = { attr: VisitAttribution; lastSeenAt: number }

function readStored(): StoredOrigin | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredOrigin
    if (!parsed?.attr || typeof parsed.lastSeenAt !== 'number') return null
    return parsed
  } catch {
    return null
  }
}

/** Origem decidida neste carregamento de documento (SPA reaproveita). */
let documentOrigin: VisitAttribution | null = null
let lastWriteAt = 0

function writeStored(attr: VisitAttribution, now: number): void {
  lastWriteAt = now
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ attr, lastSeenAt: now }))
  } catch {
    // ignore quota / modo privado
  }
}

/** Qualquer evento (tempo na página, cálculo…) mantém a sessão viva. */
function touchSession(): void {
  if (!documentOrigin) return
  const now = Date.now()
  if (now - lastWriteAt < 15_000) return
  writeStored(documentOrigin, now)
}

/**
 * Grava a origem na primeira página do documento; nas navegações seguintes
 * (SPA) só renova a atividade da sessão e devolve a mesma origem.
 */
export function captureVisitOrigin(landingPath: string): VisitAttribution {
  if (typeof window === 'undefined') return directAttribution(landingPath)

  const now = Date.now()

  if (documentOrigin) {
    writeStored(documentOrigin, now)
    return documentOrigin
  }

  const fresh = resolveLandingOrigin({
    search: window.location.search,
    referrer: document.referrer,
    landingPath,
    currentHost: window.location.hostname,
  })

  let attr: VisitAttribution
  if (fresh) {
    attr = fresh
  } else {
    // Sem origem própria: herda a sessão ativa (outra aba, reload, retorno do
    // login Google). Sessão expirada → entrada direta, nunca "o próprio site".
    const stored = readStored()
    attr =
      stored && now - stored.lastSeenAt < SESSION_TIMEOUT_MS
        ? stored.attr
        : directAttribution(landingPath)
  }

  documentOrigin = attr
  writeStored(attr, now)
  return attr
}

export function getVisitOrigin(): VisitAttribution | null {
  if (typeof window === 'undefined') return null
  if (documentOrigin) return documentOrigin
  // Evento disparado antes do primeiro page view (ex.: tracker de componente
  // filho): decide a origem agora, com a URL/referrer de chegada.
  return captureVisitOrigin(normalizeTelemetryPath(window.location.pathname))
}

/** Propriedades anexadas a cada evento de telemetria. */
export function originProperties(): Record<string, string | null> {
  const o = getVisitOrigin()
  if (!o) return {}
  touchSession()
  return {
    traffic_source: o.source,
    traffic_source_label: o.sourceLabel,
    traffic_medium: o.medium,
    traffic_medium_label: o.mediumLabel,
    traffic_campaign: o.campaign,
    referrer_host: o.referrerHost,
    ...(o.clickId ? { traffic_click_id: o.clickId } : {}),
  }
}

/** Extrai rótulo legível de properties gravadas no servidor. */
export function originFromProperties(properties: unknown): {
  label: string
  detail: string | null
} {
  if (!properties || typeof properties !== 'object') {
    return { label: 'Entrada direta', detail: null }
  }
  const p = properties as Record<string, unknown>

  // Dados antigos: navegação interna era gravada como "Entrada direta" com
  // referrer_host = calculamei.com.br. O próprio site nunca é origem.
  const referrerHost =
    typeof p.referrer_host === 'string' && !isOwnHost(p.referrer_host) ? p.referrer_host : null
  if (p.traffic_source === 'internal') {
    return { label: 'Entrada direta', detail: null }
  }

  const label =
    typeof p.traffic_source_label === 'string'
      ? p.traffic_source_label
      : typeof p.traffic_source === 'string'
        ? labelSource(p.traffic_source)
        : 'Entrada direta'

  const parts: string[] = []
  if (typeof p.traffic_medium_label === 'string') parts.push(p.traffic_medium_label)
  else if (typeof p.traffic_medium === 'string') {
    const m = labelMedium(p.traffic_medium)
    if (m) parts.push(m)
  }
  if (typeof p.traffic_campaign === 'string') parts.push(`Campanha: ${p.traffic_campaign}`)
  if (referrerHost && parts.length === 0) {
    parts.push(`Veio de ${referrerHost}`)
  }

  return {
    label,
    detail: parts.length > 0 ? parts.join(' · ') : null,
  }
}

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s} seg`
  const m = Math.floor(s / 60)
  const rest = s % 60
  if (m < 60) return rest > 0 ? `${m} min ${rest} seg` : `${m} min`
  const h = Math.floor(m / 60)
  const rm = m % 60
  return rm > 0 ? `${h} h ${rm} min` : `${h} h`
}
